import { useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardCustomerProvider, useCustomerApi, useDashboardCustomer, type DashboardCustomer } from '@/contexts/DashboardCustomerContext';
import { CustomerPicker } from '@/components/dashboard/CustomerPicker';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminRole } from '@/types/user';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { motion } from 'framer-motion';
import { Shield, Users } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import RefreshIndicator from '@/components/dashboard/RefreshIndicator';
// DateRangeFilter moved to StorageChart component
import { DashboardHealthCard } from '@/components/dashboard/DashboardHealthCard';
import { DashboardStorageCard } from '@/components/dashboard/DashboardStorageCard';
import StatusCard from '@/components/dashboard/StatusCard';
import StorageChart from '@/components/dashboard/StorageChart';
import ComplianceWidget from '@/components/dashboard/ComplianceWidget';
import HAPairsTable from '@/components/dashboard/HAPairsTable';
import CapacityGauge from '@/components/dashboard/CapacityGauge';
import RiskMatrix from '@/components/dashboard/RiskMatrix';
import EnergyMetrics from '@/components/dashboard/EnergyMetrics';
import TicketsWidget from '@/components/dashboard/TicketsWidget';
import AutoSupportWidget from '@/components/dashboard/AutoSupportWidget';
import { IBReviewAction } from '@/components/dashboard/IBReviewAction';
import { SkeletonLoader } from '@/components/common/SkeletonLoader';
import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

// Individual data loader components
const HACard = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/dashboard/ha-pairs');
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch HA pairs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  if (loading) return <SkeletonLoader variant="card" />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <StatusCard
        title="HA Pairs"
        value={`${data?.summary?.active_pairs || 0}/${data?.total_pairs || 0}`}
        status={data?.total_pairs > 0 ? 'healthy' : 'warning'}
        icon={<Users className="w-6 h-6 text-blue-500" />}
        subtitle={`${data?.summary?.metrocluster_pairs || 0} MetroCluster`}
        tooltip="High Availability (HA) pairs provide automatic failover protection. Active pairs indicate healthy redundancy. MetroCluster provides disaster recovery across geographically separated sites."
      />
    </motion.div>
  );
};

const DRCard = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/dashboard/dr-status');
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch DR status:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  if (loading) return <SkeletonLoader variant="card" />;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'ready':
        return 'text-green-500';
      case 'warning':
      case 'partial':
        return 'text-yellow-500';
      case 'critical':
      case 'not_ready':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <StatusCard
        title="DR Readiness"
        value={`${data?.dr_readiness_score || 0}%`}
        status={data?.status || 'partial'}
        icon={<Shield className={`w-6 h-6 ${getStatusColor(data?.status)}`} />}
        subtitle={`${data?.metrics?.clusters || 0} clusters configured`}
        tooltip="Disaster Recovery (DR) readiness score indicates the percentage of your storage environment protected by replication and backup strategies. Higher scores mean better protection against data loss."
      />
    </motion.div>
  );
};

const StorageAnalytics = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const apiClient = useCustomerApi();
  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingGauge, setLoadingGauge] = useState(true);
  const [storageData, setStorageData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/dashboard/storage');
        setStorageData(response.data);
      } catch (err) {
        console.error('Failed to fetch storage analytics:', err);
      } finally {
        setLoadingChart(false);
        setLoadingGauge(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <motion.div
        className="lg:col-span-7"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {loadingChart ? (
          <SkeletonLoader variant="chart" />
        ) : (
          <StorageChart data={storageData} />
        )}
      </motion.div>

      <motion.div
        className="lg:col-span-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {loadingGauge ? (
          <SkeletonLoader variant="chart" />
        ) : (
          <CapacityGauge
            used={storageData?.summary?.total_used_tb || 0}
            total={storageData?.summary?.total_raw_tb || 1}
            usable={storageData?.summary?.total_usable_tb || 0}
            clusters={storageData?.clusters || []}
          />
        )}
      </motion.div>
    </div>
  );
};

const ComplianceSection = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/dashboard/compliance');
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch compliance data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  if (loading) return <SkeletonLoader variant="chart" className="h-48" />;

  return (
    <motion.div
      id="security-compliance-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="transition-all duration-300"
    >
      <ComplianceWidget data={data} />
    </motion.div>
  );
};

const HealthWellnessSection = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const apiClient = useCustomerApi();
  const [loadingRisk, setLoadingRisk] = useState(true);
  const [loadingWellness, setLoadingWellness] = useState(true);
  const [complianceData, setComplianceData] = useState<any>(null);
  const [healthData, setHealthData] = useState<any>(null);
  const [drData, setDrData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [compliance, health, dr] = await Promise.all([
          apiClient.get('/dashboard/compliance'),
          apiClient.get('/dashboard/health'),
          apiClient.get('/dashboard/dr-status')
        ]);
        setComplianceData(compliance.data);
        setHealthData(health.data);
        setDrData(dr.data);
      } catch (err) {
        console.error('Failed to fetch health/wellness data:', err);
      } finally {
        setLoadingRisk(false);
        setLoadingWellness(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {loadingRisk ? (
          <SkeletonLoader variant="chart" />
        ) : (
          <RiskMatrix risks={complianceData?.risk_summary} />
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {loadingWellness ? (
          <SkeletonLoader variant="chart" />
        ) : (
          <div className="bg-white dark:bg-slate-800/95 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              System Wellness
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Active Contracts</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {healthData?.metrics?.systems?.active_contracts || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Expired Contracts</span>
                <span className={`font-semibold ${
                  healthData?.metrics?.systems?.expired_contracts > 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {healthData?.metrics?.systems?.expired_contracts || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Total Clusters</span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  {healthData?.metrics?.systems?.clusters || 0}
                </span>
              </div>
              {drData?.recommendations && drData.recommendations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Recommendations
                  </h4>
                  <ul className="space-y-1">
                    {drData.recommendations.map((rec: string, idx: number) => (
                      <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 flex items-start">
                        <AlertTriangle className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

const HATableSection = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/dashboard/ha-pairs');
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch HA pairs table:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  if (loading) return <SkeletonLoader variant="table" lines={5} />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <HAPairsTable data={data} />
    </motion.div>
  );
};

// Energy Metrics Section
const EnergySection = ({ refreshTrigger }: { refreshTrigger?: number }) => {
  const apiClient = useCustomerApi();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/dashboard/energy');
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch energy metrics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshTrigger]);

  if (loading) return <SkeletonLoader variant="card" />;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <EnergyMetrics data={data} />
    </motion.div>
  );
};

function CustomerDashboard() {
  const customer = useDashboardCustomer();
  const { user } = useAuth();
  const canRefresh = isAdminRole(user?.role);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [collection, setCollection] = useState<(DashboardCustomer & { has_data: boolean }) | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const requestVersion = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const [starting, setStarting] = useState(false);
  const [hasCachedData, setHasCachedData] = useState<boolean | null>(null);

  const loadCustomer = useCallback(async (force = false) => {
    const version = ++requestVersion.current;
    clearTimeout(timer.current);
    setRequestError(null);
    setStarting(true);
    const url = `/dashboard/customers/${encodeURIComponent(customer.customer_id)}/collection`;
    const receive = async (data: DashboardCustomer & { has_data: boolean }) => {
      if (version !== requestVersion.current) return;
      setCollection(data);
      setHasCachedData(previous => data.collection_status !== 'running' ? data.has_data : previous ?? data.has_data);
      setStarting(false);
      if (data.collection_status === 'running' || data.collection_status === 'pending') {
        timer.current = setTimeout(async () => {
          try {
            const response = await apiClient.get(url);
            await receive(response.data);
          } catch {
            if (version === requestVersion.current) setRequestError('Unable to check collection progress. Please retry.');
          }
        }, 2000);
      } else {
        setRefreshTrigger(value => value + 1);
      }
    };
    try {
      const response = force
        ? await apiClient.post(url, {}, { params: { force: true } })
        : await apiClient.get(url);
      await receive(response.data);
    } catch {
      if (version === requestVersion.current) {
        setStarting(false);
        setRequestError('Unable to load this customer. Please retry.');
      }
    }
  }, [customer.customer_id]);

  useEffect(() => {
    void loadCustomer();
    return () => {
      requestVersion.current += 1;
      clearTimeout(timer.current);
    };
  }, [loadCustomer]);

  const isCollecting = starting || collection?.collection_status === 'running' || collection?.collection_status === 'pending';
  const collectionError = requestError || collection?.collection_error;
  const canShowData = !!collection?.has_data && (hasCachedData || collection.collection_status !== 'running');

  // Pause polling on error only for users who can act on it; viewers keep the
  // cheap GET running so they pick up an administrator's retry.
  useEffect(() => {
    if (isModalOpen || isCollecting || (collectionError && canRefresh)) return;
    const interval = setInterval(() => void loadCustomer(), 60000);
    return () => clearInterval(interval);
  }, [isModalOpen, isCollecting, collectionError, canRefresh, loadCustomer]);

  return (
    <ErrorBoundary>
        <div className="space-y-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customer.customer_name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Customer ID: {customer.customer_id}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <IBReviewAction disabled={!canShowData} />
                <RefreshIndicator
                  lastRefresh={collection?.last_collected_at ? new Date(collection.last_collected_at) : null}
                  isRefreshing={!!isCollecting && !requestError}
                  onRefresh={() => void loadCustomer(true)}
                  autoRefreshInterval={60000}
                  canRefresh={canRefresh}
                />
              </div>
            </div>
          </div>

          {collectionError && (
            <div role="alert" className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-4 text-amber-800 dark:text-amber-200">
              {collectionError} {canShowData && 'Showing available data.'}
              {canRefresh
                ? <button className="ml-3 underline" onClick={() => void loadCustomer(true)}>Retry collection</button>
                : <span className="ml-3">Ask an administrator to retry the collection.</span>}
            </div>
          )}
          {isCollecting && !requestError && <p role="status" className="text-gray-600 dark:text-gray-400">
            Preparing data for {customer.customer_name} in the background. Stored data remains available during refreshes.
          </p>}
          {!collection && !requestError && <SkeletonLoader variant="chart" />}
          {collection && !canShowData && !isCollecting && !collectionError && (
            <p className="p-6 text-gray-600 dark:text-gray-400">No systems were returned by NetApp for this customer.</p>
          )}
          {canShowData && <>
          {/* Row 1: Status Cards (4 columns) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <DashboardHealthCard
              refreshTrigger={refreshTrigger}
            />
            <HACard refreshTrigger={refreshTrigger} />
            <DashboardStorageCard
              refreshTrigger={refreshTrigger}
            />
            <DRCard refreshTrigger={refreshTrigger} />
          </div>

          {/* Row 2: Analytics */}
          <StorageAnalytics refreshTrigger={refreshTrigger} />

          {/* Row 3: Security & Compliance */}
          <ComplianceSection refreshTrigger={refreshTrigger} />

          {/* Row 4: Energy & Environmental Metrics */}
          <EnergySection refreshTrigger={refreshTrigger} />

          {/* Row 5: Support Tickets */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Helpdesk overview · All accounts (independent of the selected customer)</p>
            <TicketsWidget
              refreshTrigger={refreshTrigger}
              onModalStateChange={setIsModalOpen}
            />
          </motion.div>

          {/* AutoSupport Status Widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <AutoSupportWidget refreshTrigger={refreshTrigger} />
          </motion.div>

          {/* Row 6: Health & Wellness */}
          <HealthWellnessSection refreshTrigger={refreshTrigger} />

          {/* Row 6: HA Pairs Detail Table */}
          <HATableSection refreshTrigger={refreshTrigger} />
          </>}
        </div>
    </ErrorBoundary>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const [customers, setCustomers] = useState<DashboardCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get('customer');
  const selected = customers.find(customer => customer.customer_id === requestedId)
    || (!requestedId ? customers[0] : undefined);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient.get('/dashboard/customers').then(({ data }) => {
      if (cancelled) return;
      setCustomers(data.customers);
      setWarning(data.warning);
    }).catch(() => {
      if (!cancelled) setError('Unable to load customers. Please try again.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [reload]);

  const picker = !loading && !error && customers.length > 0 ? (
    <CustomerPicker customers={customers} selectedId={selected?.customer_id || ''} onChange={id => {
      if (id === selected?.customer_id) return;
      setSearchParams(previous => { const next = new URLSearchParams(previous); next.set('customer', id); return next; });
    }} />
  ) : undefined;

  return (
    <DashboardLayout showCustomerLogo={false} headerSlot={picker}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">NetApp Storage Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">Choose a customer from the picker in the header.</p>
        </div>
        {loading ? <p role="status">Loading customers…</p> : error ? (
          <div role="alert">{error} <button className="underline" onClick={() => setReload(value => value + 1)}>Retry</button></div>
        ) : <>
          {warning && <div role="alert" className="text-amber-700 dark:text-amber-300">{warning} <button className="underline" onClick={() => setReload(value => value + 1)}>Retry discovery</button></div>}
          {customers.length === 0 ? (
            isAdmin
              ? <p>No customers are available. <a href="/settings/netapp-config" className="text-blue-600 underline">Set up a NetApp connection</a> to get started.</p>
              : <p>No customers are available yet.</p>
          ) : selected ? (
            <DashboardCustomerProvider key={selected.customer_id} customer={selected}>
              <CustomerDashboard />
            </DashboardCustomerProvider>
          ) : <p role="alert">This customer is no longer available. Select another customer in the header.</p>}
        </>}
      </div>
    </DashboardLayout>
  );
}
